import React, { useEffect, useState, useMemo } from "react";
import {
    Search,
    Calendar,
    CreditCard,
    CheckCircle,
    XCircle,
    Clock,
    Copy,
    Loader2,
    ArrowLeft,
    ChevronRight,
    Filter
} from "lucide-react";
import useConsultantSubscriptionStore from "../../store/consultant/useConsultantSubscriptionStore";
import { format } from "date-fns";
import ConsultantLayout from "../../layout/consultant/ConsultantLayout";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";

const ConsultantSubscriptionHistory = () => {
    const navigate = useNavigate();
    const { history, isLoading, fetchHistory } = useConsultantSubscriptionStore();
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("active");

    useEffect(() => {
        fetchHistory();
    }, []);

    const copyToClipboard = (text, type = "ID") => {
        navigator.clipboard.writeText(text);
        toast.success(`${type} copied to clipboard`);
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        try {
            return format(new Date(dateString), "dd MMM yyyy");
        } catch {
            return "Invalid Date";
        }
    };

    const getStatusStyles = (status) => {
        const s = status?.toLowerCase();
        if (s === "active" || s === "success") return "bg-emerald-100 text-emerald-700 border-emerald-200";
        if (s === "pending") return "bg-amber-100 text-amber-700 border-amber-200";
        if (s === "failed" || s === "cancelled") return "bg-rose-100 text-rose-700 border-rose-200";
        return "bg-slate-100 text-slate-700 border-slate-200";
    };

    const filteredHistory = useMemo(() => {
        return history.filter(item => {
            const matchesSearch =
                item.merchant_order_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.cashfree_order_id?.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesStatus = statusFilter === "all" || item.status?.toLowerCase() === statusFilter;

            return matchesSearch && matchesStatus;
        });
    }, [history, searchTerm, statusFilter]);

    return (
        <ConsultantLayout>
            <div className="min-h-screen bg-gray-50 flex flex-col">
                {/* Header Section */}
                <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={() => navigate("/consultant/subscription")}
                                    className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500"
                                >
                                    <ArrowLeft size={20} />
                                </button>
                                <div>
                                    <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Subscription History</h1>
                                    <p className="text-sm text-gray-500">View and manage your past transactions</p>
                                </div>
                            </div>

                            <div className="flex items-center gap-2 text-sm text-gray-500 bg-blue-50 px-4 py-2 rounded-xl border border-blue-100">
                                <CheckCircle size={16} className="text-blue-600" />
                                <span className="font-semibold text-blue-800">{history.length}</span>
                                <span>Total Transactions</span>
                            </div>
                        </div>

                        {/* Search and Filters Bar */}
                        <div className="mt-6 flex flex-col md:flex-row gap-4">
                            <div className="relative flex-1 group">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors" size={18} />
                                <input
                                    type="text"
                                    placeholder="Search by Order ID or Payment ID..."
                                    className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none text-sm"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>

                            <div className="flex gap-2">
                                <div className="relative min-w-[140px]">
                                    <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                    <select
                                        className="w-full pl-9 pr-8 py-2.5 bg-gray-50 border border-gray-200 rounded-xl appearance-none focus:ring-2 focus:ring-blue-500/20 outline-none text-sm font-medium text-gray-700"
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                    >
                                        <option value="all">All Status</option>
                                        <option value="active">Active</option>
                                        <option value="pending">Pending</option>
                                        <option value="failed">Failed</option>
                                    </select>
                                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                                        <ChevronRight size={14} className="rotate-90 text-gray-400" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Content Area */}
                <div className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center py-24">
                            <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
                            <p className="mt-4 text-gray-500 font-medium">Crunching transaction data...</p>
                        </div>
                    ) : filteredHistory.length === 0 ? (
                        <div className="bg-white rounded-2xl border border-gray-200 p-16 text-center shadow-sm max-w-lg mx-auto">
                            <div className="bg-gray-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                                <CreditCard className="text-gray-300 w-10 h-10" />
                            </div>
                            <h2 className="text-xl font-bold text-gray-900">No transactions found</h2>
                            <p className="text-gray-500 mt-2">
                                {searchTerm || statusFilter !== "all"
                                    ? "Try adjusting your filters to find what you're looking for."
                                    : "Take the first step and secure your consultant presence today."}
                            </p>
                            {!searchTerm && statusFilter === "all" && (
                                <button
                                    onClick={() => navigate("/consultant/subscription")}
                                    className="mt-8 px-6 py-2.5 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-colors shadow-lg shadow-blue-200"
                                >
                                    View Subscription Plans
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {/* Desktop Table */}
                            <div className="hidden lg:block bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
                                <table className="w-full text-left">
                                    <thead className="bg-gray-50 border-b border-gray-100">
                                        <tr>
                                            <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-widest">Date</th>
                                            <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-widest">Transaction IDs</th>
                                            <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-widest">Amount</th>
                                            <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-widest">Status</th>
                                            <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-widest">Validity</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {filteredHistory.map((item) => (
                                            <tr key={item.id} className="hover:bg-blue-50/30 transition-colors group">
                                                <td className="px-6 py-5">
                                                    <div className="flex flex-col">
                                                        <span className="text-sm font-bold text-gray-900">{formatDate(item.created_at)}</span>
                                                        <span className="text-[10px] text-gray-400 mt-0.5">{format(new Date(item.created_at), "hh:mm a")}</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-5">
                                                    <div className="flex flex-col gap-1.5">
                                                        <div className="flex items-center gap-2 group/id">
                                                            <span className="text-xs font-mono text-gray-500 bg-gray-50 px-2 py-0.5 rounded border border-gray-100">
                                                                #{item.merchant_order_id}
                                                            </span>
                                                            <button
                                                                onClick={() => copyToClipboard(item.merchant_order_id, "Order ID")}
                                                                className="opacity-0 group-hover/id:opacity-100 p-1 hover:text-blue-600 transition-all"
                                                            >
                                                                <Copy size={12} />
                                                            </button>
                                                        </div>
                                                        {item.cashfree_order_id && (
                                                            <div className="flex items-center gap-2 group/pay">
                                                                <span className="text-[10px] font-mono text-gray-400 italic">
                                                                    Pay ID: {item.cashfree_order_id}
                                                                </span>
                                                                <button
                                                                    onClick={() => copyToClipboard(item.cashfree_order_id, "Payment ID")}
                                                                    className="opacity-0 group-hover/pay:opacity-100 p-1 hover:text-blue-600 transition-all"
                                                                >
                                                                    <Copy size={10} />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-5">
                                                    <span className="text-sm font-black text-gray-900 font-mono">
                                                        ₹{Number(item.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-5">
                                                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold border ${getStatusStyles(item.status)} uppercase tracking-tight`}>
                                                        {item.status === 'active' ? <CheckCircle size={10} className="mr-1" /> : (item.status === 'pending' ? <Clock size={10} className="mr-1" /> : <XCircle size={10} className="mr-1" />)}
                                                        {item.status}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-5">
                                                    {item.status === 'active' && item.validity_end_date ? (
                                                        <div className="flex flex-col">
                                                            <span className="text-xs font-semibold text-emerald-700">Ends {formatDate(item.validity_end_date)}</span>
                                                            <span className="text-[10px] text-gray-400">{item.duration_months} Months Plan</span>
                                                        </div>
                                                    ) : (
                                                        <span className="text-xs text-gray-400 italic">N/A</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile Grid Layout */}
                            <div className="lg:hidden space-y-4">
                                {filteredHistory.map((item) => (
                                    <div key={item.id} className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm active:scale-[0.98] transition-transform">
                                        <div className="flex justify-between items-start mb-4">
                                            <div className="flex flex-col">
                                                <span className="text-xs font-bold text-gray-500 uppercase tracking-tighter mb-1">{formatDate(item.created_at)}</span>
                                                <span className="text-xl font-black text-gray-900 whitespace-nowrap">₹{Number(item.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                                            </div>
                                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold border ${getStatusStyles(item.status)} uppercase tracking-tight`}>
                                                {item.status}
                                            </span>
                                        </div>

                                        <div className="space-y-3 bg-gray-50 rounded-xl p-4 border border-gray-100">
                                            <div className="flex justify-between items-center text-xs">
                                                <span className="text-gray-500">Order ID</span>
                                                <button
                                                    onClick={() => copyToClipboard(item.merchant_order_id)}
                                                    className="flex items-center gap-1.5 font-mono font-medium text-gray-800"
                                                >
                                                    #{item.merchant_order_id.slice(-8)}
                                                    <Copy size={10} className="text-blue-500" />
                                                </button>
                                            </div>

                                            {item.status === 'active' && (
                                                <div className="flex justify-between items-center text-xs pt-2 border-t border-gray-200/50">
                                                    <span className="text-gray-500">Plan Status</span>
                                                    <span className="font-bold text-emerald-700">Valid until {formatDate(item.validity_end_date)}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </ConsultantLayout>
    );
};

export default ConsultantSubscriptionHistory;
